import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Loader2,
  LogOut,
  Trash2,
  MailOpen,
  Mail,
  RefreshCw,
  Inbox,
  Lock,
} from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "avirova_admin_token";

const inputCls =
  "h-12 w-full rounded-xl border border-zinc-800 bg-ink-900/70 px-4 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors focus:border-volt/50";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contact`, authHeaders());
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setEnquiries(data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) logout();
      else setError("Could not load enquiries. Please try again.");
    }
  }, [authHeaders, logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (ev) => {
    ev.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id, read) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { read }, authHeaders());
      setEnquiries((list) => list.map((q) => (q.id === id ? { ...q, read } : q)));
    } catch {
      setError("Action failed. Please try again.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, authHeaders());
      setEnquiries((list) => list.filter((q) => q.id !== id));
    } catch {
      setError("Delete failed. Please try again.");
    }
  };

  return (
    <>
      <Seo title="Admin — Enquiries | Avirova Technologies" description="Private admin area." />
      <section className="mx-auto max-w-5xl px-5 pt-36 pb-24 sm:px-8 lg:pt-40" data-testid="admin-page">
        {!token ? (
          <div className="mx-auto max-w-md">
            <div className="rounded-2xl border border-zinc-800 bg-ink-900/60 p-8 sm:p-10">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-zinc-800 bg-ink-950 text-volt">
                <Lock className="h-5 w-5" />
              </span>
              <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tighter text-zinc-100">
                Admin sign in
              </h1>
              <p className="mt-2 text-sm text-zinc-500">Private area. Authorised access only.</p>
              <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
                <div>
                  <label htmlFor="admin-email" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Email</label>
                  <input id="admin-email" type="email" required data-testid="admin-email-input" className={`mt-2.5 ${inputCls}`} placeholder="you@avirova.tech" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="admin-password" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Password</label>
                  <input id="admin-password" type="password" required data-testid="admin-password-input" className={`mt-2.5 ${inputCls}`} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                {error && <p data-testid="admin-login-error" className="rounded-xl border border-red-400/30 bg-red-400/[0.06] px-4 py-3 text-sm text-red-300">{error}</p>}
                <button type="submit" data-testid="admin-login-button" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-volt px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign In"}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">{"// Admin"}</p>
                <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter text-zinc-100">
                  Consultation requests
                </h1>
              </div>
              <div className="flex gap-3">
                <button onClick={load} data-testid="admin-refresh-button" className="flex items-center gap-2 rounded-full border border-zinc-700 px-5 py-2.5 text-sm font-semibold text-zinc-200 transition-colors duration-300 hover:border-volt/50 hover:text-volt">
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
                <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-zinc-700 px-5 py-2.5 text-sm font-semibold text-zinc-200 transition-colors duration-300 hover:border-red-400/50 hover:text-red-300">
                  <LogOut className="h-4 w-4" /> Log out
                </button>
              </div>
            </div>

            {error && <p data-testid="admin-error-message" className="mt-6 rounded-xl border border-red-400/30 bg-red-400/[0.06] px-4 py-3 text-sm text-red-300">{error}</p>}

            <div className="mt-8 space-y-3">
              {enquiries === null ? (
                <div className="flex items-center gap-3 py-16 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading enquiries…</div>
              ) : enquiries.length === 0 ? (
                <div className="flex flex-col items-center gap-4 rounded-2xl border border-zinc-800 bg-ink-900/60 py-20 text-zinc-500" data-testid="admin-empty-state">
                  <Inbox className="h-8 w-8" />
                  <p className="text-sm">No enquiries yet.</p>
                </div>
              ) : (
                enquiries.map((q) => (
                  <div key={q.id} data-testid={`enquiry-row-${q.id}`} className={`rounded-2xl border bg-ink-900/60 transition-colors duration-300 ${q.read ? "border-zinc-800" : "border-volt/30"}`}>
                    <button
                      onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                      data-testid={`enquiry-toggle-${q.id}`}
                      className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 px-6 py-5 text-left"
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-full ${q.read ? "bg-zinc-700" : "bg-volt animate-pulse-dot"}`} aria-label={q.read ? "Read" : "Unread"} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-display text-lg font-bold tracking-tight text-zinc-100">{q.name}</span>
                        <span className="block truncate font-mono text-xs text-zinc-500">{q.email}{q.company ? ` · ${q.company}` : ""}</span>
                      </span>
                      {q.testing_requirement && (
                        <span className="rounded-md border border-zinc-800 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400">{q.testing_requirement}</span>
                      )}
                      <span className="font-mono text-[11px] text-zinc-600">{formatDate(q.created_at)}</span>
                    </button>
                    {expanded === q.id && (
                      <div className="border-t border-zinc-800 px-6 py-6" data-testid={`enquiry-detail-${q.id}`}>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {[
                            ["Website", q.website],
                            ["Company size", q.company_size],
                            ["Current QA setup", q.qa_setup],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">{label}</p>
                              <p className="mt-1 text-sm text-zinc-300">{value || "—"}</p>
                            </div>
                          ))}
                        </div>
                        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Message</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{q.message}</p>
                        <div className="mt-6 flex flex-wrap gap-3">
                          <a href={`mailto:${q.email}`} data-testid={`enquiry-reply-${q.id}`} className="flex items-center gap-2 rounded-full bg-volt px-5 py-2.5 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.02]">
                            <Mail className="h-3.5 w-3.5" /> Reply by email
                          </a>
                          <button onClick={() => markRead(q.id, !q.read)} data-testid={`enquiry-read-toggle-${q.id}`} className="flex items-center gap-2 rounded-full border border-zinc-700 px-5 py-2.5 text-xs font-semibold text-zinc-300 transition-colors duration-300 hover:border-volt/50 hover:text-volt">
                            <MailOpen className="h-3.5 w-3.5" /> {q.read ? "Mark unread" : "Mark read"}
                          </button>
                          <button onClick={() => remove(q.id)} data-testid={`enquiry-delete-${q.id}`} className="flex items-center gap-2 rounded-full border border-zinc-700 px-5 py-2.5 text-xs font-semibold text-zinc-300 transition-colors duration-300 hover:border-red-400/50 hover:text-red-300">
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
